import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { LevelBadge } from "@/components/LevelBadge";
import { MobileTabBar } from "@/components/MobileTabBar";
import { InstallPrompt } from "@/components/InstallPrompt";
import { SupportChat } from "@/components/SupportChat";
import {
  LogOut,
  LayoutDashboard,
  Trophy,
  Target,
  Shield,
  Wrench,
  ShoppingBag,
  MessageSquare,
  Newspaper,
  ShieldCheck,
  Users,
  Crown,
  MoreHorizontal,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [payMode, setPayMode] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    api.get("/payments/mode").then(({ data }) => setPayMode(data)).catch(() => {});
  }, []);

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const [adminBadge, setAdminBadge] = useState(0);
  useEffect(() => {
    if (!user?.is_admin) return;
    const load = () => api.get("/admin/inbox-badges")
      .then(({ data }) => setAdminBadge(data.total || 0)).catch(() => {});
    load();
    const onBadges = (e) => setAdminBadge(e.detail?.total || 0);
    window.addEventListener("admin-badges", onBadges);
    const t = setInterval(load, 120000);
    return () => { clearInterval(t); window.removeEventListener("admin-badges", onBadges); };
  }, [user?.is_admin, location.pathname]);

  const primaryNav = [
    { to: "/friends", label: "Friends Only", icon: Users },
    { to: "/lobby", label: "Lobby", icon: Target },
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/entries", label: "My Entries", icon: Trophy },
    ...(user?.is_admin ? [{ to: "/admin", label: "Admin", icon: Wrench, badge: adminBadge }] : []),
  ];
  const moreNav = [
    { to: "/manager", label: "Manager Hub", icon: Crown },
    { to: "/managers", label: "Top Hosts", icon: Trophy },
    { to: "/blog", label: "Previews", icon: Newspaper },
    { to: "/merch", label: "Merch", icon: ShoppingBag },
    { to: "/legal", label: "Legal", icon: Shield },
    { to: "/contact", label: "Contact", icon: MessageSquare },
  ];
  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {payMode && payMode.collects_entry_fees === false && (
        <div className="bg-[#00FF66] text-black" data-testid="no-fees-banner">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-1.5 flex items-center justify-center gap-2 text-center">
            <ShieldCheck size={13} className="flex-shrink-0" />
            <span className="text-[10px] md:text-xs uppercase tracking-widest font-bold">
              Free skills challenges · no entry fees · no cash prizes
            </span>
            {user?.is_admin && (
              <span className="hidden lg:inline text-xs font-semibold">
                Stripe sells subscriptions only ({payMode.commerce?.mode})
              </span>
            )}
          </div>
        </div>
      )}

      <nav className="sticky top-0 z-40 border-b border-white/10 bg-black/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-3">
          <button
            data-testid="nav-logo"
            onClick={() => navigate("/lobby")}
            className="flex items-center gap-2 min-w-0 md:flex-shrink-0 hover:opacity-80"
          >
            <img
              src="/brand-logo.png"
              alt="PickemSports.club"
              className="w-9 h-9 md:w-10 md:h-10 rounded-full flex-shrink-0"
            />
            <span className="font-display text-lg md:text-2xl tracking-widest truncate md:overflow-visible md:text-clip">
              PICKEMSPORTS<span className="text-[#00FF66]">.CLUB</span>
            </span>
          </button>

          <div className="hidden md:flex items-center gap-1">
            {primaryNav.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`px-2.5 lg:px-3 py-2 text-xs lg:text-sm uppercase tracking-widest font-semibold flex items-center gap-1.5 rounded-sm whitespace-nowrap transition-colors ${
                    isActive(item.to) ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <Icon size={14} />
                  {item.label}
                  {item.badge > 0 && (
                    <span
                      data-testid="nav-admin-badge"
                      className="min-w-[16px] h-4 px-1 rounded-full bg-[#FF3B30] text-white text-[9px] font-bold inline-flex items-center justify-center"
                    >
                      {item.badge > 9 ? "9+" : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
            <div className="relative">
              <button
                onClick={() => setMoreOpen((v) => !v)}
                data-testid="nav-more-btn"
                className={`px-2.5 lg:px-3 py-2 text-xs lg:text-sm uppercase tracking-widest font-semibold flex items-center gap-1.5 rounded-sm whitespace-nowrap ${
                  moreOpen ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                <MoreHorizontal size={14} /> More
              </button>
              {moreOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setMoreOpen(false)} />
                  <div
                    data-testid="nav-more-menu"
                    className="absolute right-0 top-full mt-1 z-40 w-52 border border-white/10 bg-[#0f0f0f] shadow-2xl py-1"
                  >
                    {moreNav.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setMoreOpen(false)}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                          className={`flex items-center gap-2 px-3 py-2.5 text-xs uppercase tracking-widest font-semibold ${
                            isActive(item.to) ? "text-[#00FF66] bg-white/5" : "text-white/70 hover:text-white hover:bg-white/5"
                          }`}
                        >
                          <Icon size={14} /> {item.label}
                        </Link>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            {user && (
              <>
                <NotificationBell />
                <button
                  onClick={() => navigate("/wallet")}
                  data-testid="nav-wallet"
                  className="text-right active:opacity-70 hover:opacity-80"
                >
                  <div className="text-[10px] md:text-xs text-white/40 uppercase tracking-widest leading-none">
                    Wallet
                  </div>
                  <div className="font-display text-base md:text-lg text-white/50 leading-tight">
                    ${(user.wallet_balance || 0).toFixed(2)}
                  </div>
                </button>
                <div className="hidden sm:block">
                  <LevelBadge level={user.level} testId="nav-level-badge" />
                </div>
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 md:w-9 md:h-9 rounded-full border border-white/10"
                  />
                ) : (
                  <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/10 flex items-center justify-center text-sm font-semibold">
                    {user.name?.[0]?.toUpperCase()}
                  </div>
                )}
                <button
                  onClick={logout}
                  data-testid="nav-logout-btn"
                  className="hidden md:block p-2 rounded-sm hover:bg-white/10 text-white/60 hover:text-white"
                  title="Log out"
                >
                  <LogOut size={16} />
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8 pb-28 md:pb-8">{children}</main>

      {user && <MobileTabBar />}
      <SupportChat />
      <InstallPrompt />
    </div>
  );
}
